import { useEffect, useState } from "react";
import { Loader2, BarChart3, Users, Activity, DollarSign } from "lucide-react";
import Layout from "../components/Layout";
import { api } from "../lib/api";

const STAGE_LABEL = {
  intake: "Intake", cca_done: "CCA Done", plan_drafted: "Plan Drafted",
  presented: "Presented", accepted: "Accepted", claim_submitted: "Claim Submitted", paid: "Paid"
};

export default function Analytics() {
  const [tab, setTab] = useState("clinical");
  const [clinical, setClinical] = useState(null);
  const [financial, setFinancial] = useState(null);
  const [journey, setJourney] = useState(null);

  useEffect(() => {
    api.get("/analytics/clinical").then((r) => setClinical(r.data)).catch(() => {});
    api.get("/analytics/financial").then((r) => setFinancial(r.data)).catch(() => {});
    api.get("/analytics/journey").then((r) => setJourney(r.data)).catch(() => {});
  }, []);

  return (
    <Layout>
      <div className="kicker">Phase 6 · Analytics</div>
      <h1 data-testid="analytics-title" className="font-serif text-3xl text-[var(--pa-text)] mt-1 flex items-center gap-3">
        <BarChart3 size={22} className="text-[var(--pa-gold)]" /> Unified Analytics Dashboard
      </h1>

      <div className="mt-6 flex gap-2 border-b border-[var(--pa-border)]">
        {[["clinical", "Clinical KPIs", Activity], ["financial", "Financial KPIs", DollarSign], ["journey", "Patient Journey", Users]].map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`analytics-tab-${k}`}
            className={`px-4 py-2 text-sm border-b-2 -mb-px flex items-center gap-1.5 ${tab === k ? "border-[var(--pa-gold)] text-[var(--pa-gold)]" : "border-transparent text-[var(--pa-text-muted)] hover:text-[var(--pa-text)]"}`}>
            <Icon size={13} /> {label}
          </button>
        ))}
      </div>

      {tab === "clinical" && (clinical ? (
        <div className="mt-6 space-y-6" data-testid="analytics-clinical">
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(clinical.totals).map(([k, v]) => (
              <Stat key={k} label={k.replace(/_/g, " ")} value={v} />
            ))}
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <Stat label="CCA (last 30 days)" value={clinical.last_30_days.complete_case_analyses} accent />
            <Stat label="Consultations (last 30 days)" value={clinical.last_30_days.consultations} accent />
            <Stat label="Case acceptance rate" value={`${clinical.case_acceptance_rate_pct}%`} accent />
          </div>
          {clinical.module_mix?.length > 0 && (
            <div>
              <div className="kicker mb-2">Module mix</div>
              <div className="space-y-1.5">
                {clinical.module_mix.slice(0, 12).map((m) => (
                  <div key={m.module} className="flex items-center gap-3 text-xs">
                    <span className="w-48 truncate text-[var(--pa-text-muted)]">{m.module}</span>
                    <div className="flex-1 bg-[var(--pa-bg-2)] h-2 rounded overflow-hidden">
                      <div className="bg-[var(--pa-gold)] h-2" style={{ width: `${Math.min(100, m.count * 100 / Math.max(...clinical.module_mix.map(x => x.count)))}%` }} />
                    </div>
                    <span className="text-[var(--pa-text)] font-mono">{m.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : <Loader />)}

      {tab === "financial" && (financial ? (
        <div className="mt-6 space-y-6" data-testid="analytics-financial">
          <div className="grid sm:grid-cols-4 gap-3">
            <Stat label="Total claims" value={financial.claims_pipeline.total_claims} />
            <Stat label="Total billed" value={`$${financial.claims_pipeline.total_billed_usd.toLocaleString()}`} />
            <Stat label="Total paid" value={`$${financial.claims_pipeline.total_paid_usd.toLocaleString()}`} accent />
            <Stat label="Collection rate" value={`${financial.claims_pipeline.collection_rate_pct}%`} accent />
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <Stat label="Total denied" value={`$${financial.claims_pipeline.total_denied_usd.toLocaleString()}`} />
            <Stat label="Appeals drafted" value={financial.claims_pipeline.appeals_drafted} />
            <Stat label="Avg accepted case fee" value={`$${(financial.avg_accepted_case_fee_usd || 0).toLocaleString()}`} accent />
          </div>
          {Object.keys(financial.claims_pipeline.by_status || {}).length > 0 && (
            <div>
              <div className="kicker mb-2">Pipeline by status</div>
              <div className="space-y-1.5">
                {Object.entries(financial.claims_pipeline.by_status).map(([status, agg]) => (
                  <div key={status} className="flex items-center gap-3 text-xs">
                    <span className="w-40 text-[var(--pa-text-muted)] capitalize">{status.replace(/_/g, " ")}</span>
                    <span className="text-[var(--pa-text-dim)]">{agg.count} claims</span>
                    <span className="ml-auto text-[var(--pa-text)] font-mono">${agg.total_fee_usd.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {financial.top_revenue_cdt_codes?.length > 0 && (
            <div>
              <div className="kicker mb-2">Top revenue CDT codes</div>
              <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-1.5">
                {financial.top_revenue_cdt_codes.map((c) => (
                  <div key={c.cdt_code} className="text-xs bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded px-2 py-1.5 flex justify-between">
                    <span className="font-mono text-[var(--pa-gold)]">{c.cdt_code}</span>
                    <span>${c.revenue_usd.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : <Loader />)}

      {tab === "journey" && (journey ? (
        <div className="mt-6 overflow-x-auto" data-testid="analytics-journey">
          <div className="flex gap-3 min-w-max pb-4">
            {journey.stages_order.map((s) => (
              <div key={s} className="w-64 shrink-0 bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded p-3" data-testid={`stage-${s}`}>
                <div className="flex justify-between items-center mb-2">
                  <span className="font-serif text-sm text-[var(--pa-gold)]">{STAGE_LABEL[s]}</span>
                  <span className="text-xs text-[var(--pa-text-dim)]">{journey.counts[s]}</span>
                </div>
                <div className="space-y-1.5 max-h-96 overflow-y-auto">
                  {journey.columns[s].map((p) => (
                    <div key={p.patient_id} className="text-xs bg-[var(--pa-bg)] border border-[var(--pa-border)] rounded px-2 py-1.5">
                      <div className="text-[var(--pa-text)] truncate">{p.patient_name}</div>
                      <div className="text-[10px] text-[var(--pa-text-dim)]">{new Date(p.created_at).toLocaleDateString()}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : <Loader />)}
    </Layout>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`bg-[var(--pa-bg-2)] border ${accent ? "border-[var(--pa-gold)]/40" : "border-[var(--pa-border)]"} rounded p-4`}>
      <div className="text-[10px] uppercase tracking-wider text-[var(--pa-text-dim)]">{label}</div>
      <div className={`font-serif text-2xl mt-1 ${accent ? "text-[var(--pa-gold)]" : "text-[var(--pa-text)]"}`}>{value}</div>
    </div>
  );
}
function Loader() { return <div className="mt-6 flex items-center gap-2 text-[var(--pa-text-muted)] text-sm"><Loader2 size={14} className="animate-spin" /> Loading…</div>; }
